import { useState, useEffect, useRef } from 'react';
import { View, Pressable, StyleSheet, Platform, Animated, type DimensionValue } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { useTheme } from '@/theme';
import { ThemedText } from '@/components/ui/Text';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import MapsScreen from './maps';
import SafetyScreen from './safety';

traceModuleEvaluation('app/(tabs)/travel');

type TravelTab = 'maps' | 'safety';

function TravelScreenInner() {
  const theme = useTheme();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams();
  const [activeTab, setActiveTab] = useState<TravelTab>((params.tab as TravelTab) || 'maps');
  const containerRef = useRef<View>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const indicatorTranslate = useRef(new Animated.Value(0)).current;
  const [indicatorLeft, setIndicatorLeft] = useState(0);

  const tabs: Array<{ key: TravelTab; label: string; icon: keyof typeof Ionicons.glyphMap; iconActive: keyof typeof Ionicons.glyphMap }> = [
    { key: 'maps', label: 'Maps', icon: 'map-outline', iconActive: 'map' },
    { key: 'safety', label: 'Safety', icon: 'shield-checkmark-outline', iconActive: 'shield-checkmark' },
  ];
  const paramTab =
    params.tab === 'maps' || params.tab === 'safety'
      ? (params.tab as TravelTab)
      : undefined;
  const activeTabRef = useRef<TravelTab>(activeTab);

  const activeIndex = tabs.findIndex(tab => tab.key === activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  // Sync activeTab with params
  useEffect(() => {
    if (!paramTab) {
      return;
    }
    if (paramTab !== activeTabRef.current) {
      setActiveTab(paramTab);
    }
  }, [paramTab]);

  // Animate indicator position
  useEffect(() => {
    if (containerWidth === 0) {
      return;
    }

    const tabWidth = containerWidth / tabs.length;
    const target = tabWidth * activeIndex;

    if (Platform.OS === 'web') {
      // Smooth transition on web
      setIndicatorLeft(target);
      indicatorTranslate.setValue(target);
    } else {
      Animated.spring(indicatorTranslate, {
        toValue: target,
        damping: 20,
        stiffness: 200,
        mass: 0.8,
        useNativeDriver: true,
      }).start();
    }
  }, [activeIndex, containerWidth, indicatorTranslate, tabs.length, activeTab]);

  const handleTabChange = (tab: TravelTab) => {
    const isChangingTab = tab !== activeTabRef.current;
    if (isChangingTab) {
      // Haptic feedback only when tab actually changes.
      if (Platform.OS === 'ios') {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {
          // Haptics not available
        });
      }
      setActiveTab(tab);
    }
    // Update URL params only when the route is actually out of sync.
    if (paramTab !== tab) {
      try {
        router.setParams({ tab });
      } catch (error) {
        // Router may be briefly unavailable during transition; state stays updated.
      }
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
      {/* Modern Animated Tab Bar */}
      <View
        ref={containerRef}
        onLayout={(event) => {
          const { width } = event.nativeEvent.layout;
          if (width > 0) {
            setContainerWidth(width);
            const tabWidth = width / tabs.length;
            const initialPosition = tabWidth * activeIndex;
            indicatorTranslate.setValue(initialPosition);
            if (Platform.OS === 'web') {
              setIndicatorLeft(initialPosition);
            }
          }
        }}
        style={[
          styles.tabBar,
          {
            backgroundColor: theme.colors.surface,
            borderColor: theme.colors.borderMuted,
            marginTop: insets.top + 2,
            ...(Platform.OS === 'web' ? {
              boxShadow: `0 2px 8px ${theme.colors.borderMuted}20`,
            } : {
              shadowColor: theme.colors.borderMuted,
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.12,
              shadowRadius: 4,
              elevation: 3,
            }),
          },
        ]}
      >
        {/* Animated background indicator */}
        {containerWidth > 0 && (
          Platform.OS === 'web' ? (
            <View
              key={`indicator-${activeTab}-${activeIndex}`}
              style={{
                position: 'absolute',
                top: theme.spacing(0.5),
                bottom: theme.spacing(0.5),
                left: indicatorLeft + theme.spacing(0.75),
                width: `calc(${100 / tabs.length}% - ${theme.spacing(1.5)}px)` as DimensionValue,
                backgroundColor: theme.colors.primary,
                borderRadius: theme.radii.lg,
                pointerEvents: 'none',
                boxShadow: `0 4px 12px ${theme.colors.primary}40`,
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          ) : (
            <Animated.View
              key={`indicator-${activeTab}-${activeIndex}`}
              style={[
                {
                  position: 'absolute',
                  top: theme.spacing(0.5),
                  bottom: theme.spacing(0.5),
                  left: theme.spacing(0.75),
                  width: `${100 / tabs.length}%`,
                  backgroundColor: theme.colors.primary,
                  borderRadius: theme.radii.lg,
                  pointerEvents: 'none',
                  shadowColor: theme.colors.primary,
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.25,
                  shadowRadius: 8,
                  elevation: 4,
                  transform: [
                    {
                      translateX: indicatorTranslate,
                    },
                  ],
                },
              ]}
            />
          )
        )}

        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <Pressable
              key={tab.key}
              onPress={() => handleTabChange(tab.key)}
              style={({ pressed }) => [
                styles.tab,
                {
                  opacity: pressed ? 0.85 : 1,
                  transform: [{ scale: pressed ? 0.98 : 1 }],
                },
              ]}
              accessibilityRole="button"
              accessibilityLabel={`${tab.label} tab`}
              accessibilityHint={tab.key === 'maps' ? 'Switch to maps view' : 'Switch to safety and emergency view'}
              accessibilityState={{ selected: isActive }}
            >
              <Ionicons
                name={isActive ? tab.iconActive : tab.icon}
                size={isActive ? 22 : 20}
                color={isActive ? (theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF') : theme.colors.textSecondary}
              />
              <ThemedText
                variant="bodyStrong"
                style={{
                  fontSize: isActive ? 16 : 14,
                  fontWeight: isActive ? '700' : '500',
                  color: isActive ? (theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF') : theme.colors.textSecondary,
                  letterSpacing: isActive ? 0.2 : 0,
                  marginLeft: theme.spacing(1),
                }}
              >
                {tab.label}
              </ThemedText>
            </Pressable>
          );
        })}
      </View>

      {/* Tab Content - Conditionally render based on active tab */}
      <View style={styles.content}>
        <View
          pointerEvents={activeTab === 'maps' ? 'auto' : 'none'}
          style={[styles.mapPanel, activeTab === 'maps' ? styles.panelVisible : styles.panelHidden]}
        >
          <TripErrorBoundary name="TravelMapsPanel">
            <MapsScreen
              hideHeader={true}
              isActive={activeTab === 'maps'}
              tripId={typeof params.tripId === 'string' ? params.tripId : undefined}
              focusLegId={typeof params.focusLegId === 'string' ? params.focusLegId : undefined}
            />
          </TripErrorBoundary>
        </View>
        {activeTab === 'safety' ? (
          <TripErrorBoundary name="TravelSafetyPanel">
            <SafetyScreen hideHeader={true} />
          </TripErrorBoundary>
        ) : null}
      </View>
    </View>
  );
}

export default function TravelScreen() {
  return (
    <TripErrorBoundary name="TravelScreen">
      <TravelScreenInner />
    </TripErrorBoundary>
  );
}

const styles = StyleSheet.create({
  tabBar: {
    flexDirection: 'row',
    borderRadius: 16,
    padding: 6,
    borderWidth: 1,
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 6,
    position: 'relative',
    zIndex: 10,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    zIndex: 10,
    position: 'relative',
    minHeight: 48, // Accessibility: minimum touch target (increased from 44)
  },
  content: {
    flex: 1,
  },
  mapPanel: {
    flex: 1,
  },
  panelVisible: {
    display: 'flex',
  },
  panelHidden: {
    display: 'none',
  },
});

